import React from 'react';
import { Alert, Button, Form, Input, Modal, Spin } from 'antd';
import { LockOutlined, SafetyCertificateOutlined, UnlockOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../context/UserContext';
import { financeRequest, setFinanceToken } from '../api/finance';
import PaymentDrawer from './PaymentDrawer';
import { FinanceContext as Context, useFinance } from '../context/FinanceContext';
import '../pages/Finance/finance.css';


export function FinanceProvider({ children }) {
  const { user } = useUser();
  const cache = useQueryClient();
  const [unlocked, setUnlocked] = React.useState(false);
  const [event, setEvent] = React.useState(null);
  const [authOpen, setAuthOpen] = React.useState(false);
  const pendingEvent = React.useRef(null);
  const timer = React.useRef();
  const expiry = React.useRef(0);
  const channel = React.useRef();
  const clear = React.useCallback(() => {
    clearTimeout(timer.current); expiry.current = 0; setFinanceToken(null); setUnlocked(false); setEvent(null); pendingEvent.current = null;
    cache.cancelQueries({ queryKey: ['finance'] });
    cache.removeQueries({ queryKey: ['finance'] });
  }, [cache]);
  const lock = React.useCallback(async () => {
    const pending = financeRequest('post', '/lock').catch(() => {});
    clear(); channel.current?.postMessage('lock');
    await pending;
  }, [clear]);
  const touch = React.useCallback(() => { clearTimeout(timer.current); expiry.current = Date.now() + 15 * 60 * 1000; timer.current = setTimeout(lock, 15 * 60 * 1000); }, [lock]);
  React.useEffect(() => {
    clear();
    if (typeof BroadcastChannel !== 'undefined') { channel.current = new BroadcastChannel('studio-finance'); channel.current.onmessage = clear; }
    const checkExpiry = () => { if (expiry.current && Date.now() >= expiry.current) lock(); };
    document.addEventListener('visibilitychange', checkExpiry);
    window.addEventListener('finance:locked', clear); window.addEventListener('finance:activity', touch);
    return () => { clearTimeout(timer.current); setFinanceToken(null); channel.current?.close(); window.removeEventListener('finance:locked', clear); window.removeEventListener('finance:activity', touch); document.removeEventListener('visibilitychange', checkExpiry); cache.removeQueries({ queryKey: ['finance'] }); };
  }, [user?.id, clear, touch, lock, cache]);
  const requestUnlock = React.useCallback(() => setAuthOpen(true), []);
  const openPayments = selected => {
    const target = { id: selected.id, intent: selected.intent };
    if (unlocked) setEvent(target);
    else { pendingEvent.current = target; setAuthOpen(true); }
  };
  const unlock = value => {
    channel.current?.postMessage('lock'); setFinanceToken(value); setUnlocked(true); touch();
    setAuthOpen(false);
    if (pendingEvent.current) { setEvent(pendingEvent.current); pendingEvent.current = null; }
  };
  return <Context.Provider value={{ unlocked, unlock, lock, requestUnlock, openPayments }}>
    {children}
    <FinanceUnlockModal open={authOpen && !unlocked} onClose={() => { setAuthOpen(false); pendingEvent.current = null; }} />
    <PaymentDrawer event={unlocked ? event : null} onClose={() => setEvent(null)} />
  </Context.Provider>;
}
export function FinanceGate({ children }) {
  const { unlocked, requestUnlock } = useFinance();
  React.useEffect(() => { if (!unlocked) requestUnlock(); }, [unlocked, requestUnlock]);
  if (unlocked) return children;
  return <section className="finance-locked-placeholder"><LockOutlined /><h2>Payments is locked</h2><p>Enter your Payments password to continue.</p><Button type="primary" icon={<UnlockOutlined />} onClick={requestUnlock}>Unlock Payments</Button></section>;
}
function FinanceUnlockModal({ open, onClose }) {
  const { unlock } = useFinance();
  const [configured, setConfigured] = React.useState(null);
  const [reset, setReset] = React.useState(false);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [form] = Form.useForm();
  React.useEffect(() => { let active = true; if (open) { setError(''); setReset(false); } if (open) financeRequest('get', '/access').then(r => { if (active) setConfigured(r.configured); }).catch(e => { if (active) setError(e.message); }); return () => { active = false; }; }, [open, form]);
  const setup = configured === false || reset;
  return <Modal open={open} closable={!busy} maskClosable={!busy} keyboard={!busy} onCancel={() => { form.resetFields(); onClose(); }} footer={null} width={480} centered destroyOnClose title="Your payments, protected." className="finance-unlock-modal"><section className="finance-gate">
    <div className="finance-gate-icon"><SafetyCertificateOutlined /></div>
    <span className="finance-eyebrow">PRIVATE STUDIO PAYMENTS</span>
    <h2>{setup ? reset ? 'Reset your Payments password' : 'Set up your Payments password' : 'Unlock your Payments workspace'}</h2>
    <p>{setup ? 'Verify your account login password, then choose a separate password for Payments.' : 'Unlock to view revenue, manage event payments, and follow up on balances.'}</p>
    {error && <Alert type="error" showIcon message={error} />}
    {configured === null ? !error && <Spin /> : <Form form={form} layout="vertical" onFinish={async values => { setBusy(true); setError(''); try { const result = await financeRequest('post', '/unlock', { ...values, reset }); form.resetFields(); unlock(result.token); } catch (e) { setError(e.message); } finally { setBusy(false); } }}>
      {setup && <Form.Item name="loginPassword" label="Account login password" rules={[{ required: true }]}><Input.Password autoComplete="current-password" prefix={<LockOutlined />} /></Form.Item>}
      <Form.Item name="password" label={setup ? 'New Payments password' : 'Payments password'} rules={[{ required: true }, { min: 8, message: 'Use at least 8 characters.' }]}><Input.Password autoComplete={setup ? 'new-password' : 'current-password'} prefix={<LockOutlined />} /></Form.Item>
      {setup && <Form.Item name="confirm" label="Confirm Payments password" dependencies={['password']} rules={[{ required: true }, ({ getFieldValue }) => ({ validator: (_, value) => value === getFieldValue('password') ? Promise.resolve() : Promise.reject(new Error('Passwords must match.')) })]}><Input.Password autoComplete="new-password" /></Form.Item>}
      <Button type="primary" htmlType="submit" block size="large" loading={busy} icon={<UnlockOutlined />}>{setup ? 'Save password & unlock' : 'Unlock Payments'}</Button>
      {configured && <Button type="link" onClick={() => { setReset(!reset); setError(''); form.resetFields(); }}>{reset ? 'Back to unlock' : 'Forgot or change Payments password?'}</Button>}
    </Form>}
    <div className="finance-lock-note"><LockOutlined /> Locks after 15 minutes without Payments activity.</div>
  </section></Modal>;
}
